import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useEnhancedCartTracking } from '@/hooks/useEnhancedCartTracking';
import { CustomerInfo } from '@/components/relocation/CustomerInfoForm';

export interface CartItem {
  id: string;
  title: string;
  price: number;
  description?: string;
  quantity: number;
}

interface CartContextType {
  items: CartItem[];
  numberOfPeople: number;
  colaboradorCode?: string;
  addItem: (item: Omit<CartItem, 'quantity'>) => void;
  updateQuantity: (id: string, quantity: number) => void;
  removeItem: (id: string) => void;
  clearCart: () => void;
  updatePeople: (people: number) => void;
  setColaboradorCode: (code: string) => void;
  checkout: (items: CartItem[], total: number, customerInfo: CustomerInfo) => Promise<void>;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
  onCheckout?: (items: CartItem[], total: number, customerInfo: CustomerInfo) => Promise<void>;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children, onCheckout }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [numberOfPeople, setNumberOfPeople] = useState(1);
  const [colaboradorCode, setColaboradorCodeState] = useState<string>();
  const [isOpen, setIsOpen] = useState(false);
  const { trackAddToCart } = useEnhancedCartTracking();

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('punto-hispano-cart');
    if (savedCart) {
      try {
        const cartData = JSON.parse(savedCart);
        setItems(cartData.items || []);
        setNumberOfPeople(cartData.numberOfPeople || 1);
        setColaboradorCodeState(cartData.colaboradorCode);
      } catch (error) {
        console.error('Error loading cart from localStorage:', error);
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    const cartData = {
      items,
      numberOfPeople,
      colaboradorCode
    };
    localStorage.setItem('punto-hispano-cart', JSON.stringify(cartData));
  }, [items, numberOfPeople, colaboradorCode]);

  const addItem = async (newItem: Omit<CartItem, 'quantity'>) => {
    const existingItem = items.find(item => item.id === newItem.id);
    
    if (existingItem) {
      // Update quantity if item already exists
      const updatedItems = items.map(item =>
        item.id === newItem.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      );
      setItems(updatedItems);
    } else {
      // Add new item
      const itemWithQuantity = { ...newItem, quantity: 1 };
      setItems(prev => [...prev, itemWithQuantity]);
      
      // Track add to cart event
      await trackAddToCart(
        itemWithQuantity,
        { 
          items: [...items, itemWithQuantity], 
          totalAmount: 0, // Will be calculated in tracking hook
          numberOfPeople,
          colaboradorRef: colaboradorCode 
        }
      );
    }
  };

  const updateQuantity = (id: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(id);
      return;
    }
    
    setItems(prev =>
      prev.map(item =>
        item.id === id ? { ...item, quantity } : item
      )
    );
  };

  const removeItem = (id: string) => {
    setItems(prev => prev.filter(item => item.id !== id));
  };

  const clearCart = () => {
    setItems([]);
    setNumberOfPeople(1);
    setColaboradorCodeState(undefined);
  };

  const updatePeople = (people: number) => {
    setNumberOfPeople(Math.max(1, people));
  };

  const setColaboradorCode = (code: string) => {
    setColaboradorCodeState(code);
  };

  const checkout = async (items: CartItem[], total: number, customerInfo: CustomerInfo) => {
    if (onCheckout) {
      await onCheckout(items, total, customerInfo);
    }
  };

  return (
    <CartContext.Provider
      value={{
        items,
        numberOfPeople,
        colaboradorCode,
        addItem,
        updateQuantity,
        removeItem,
        clearCart,
        updatePeople,
        setColaboradorCode,
        checkout,
        isOpen,
        setIsOpen
      }}
    >
      {children}
    </CartContext.Provider>
  );
};